import {
  ControlProps,
  RankedTester,
  rankWith,
  uiTypeIs,
} from '@jsonforms/core';
import { useJsonForms, withJsonFormsEnumProps } from '@jsonforms/react';
import React, { useEffect, useState } from 'react';
import { SpectrumInputControl } from './SpectrumInputControl';
import { InputEnum } from '../spectrum-control/InputEnum';
import get from 'lodash/get';
import {
  getHeaders,
  getBaseUrl,
  formatDynamicEnumResponse,
  getEndpointName,
  getFieldNameFromPathPattern,
  getIndexFromPathPattern,
} from '../util/commonUtils';

export const SpectrumConditionalEnumControl = (props: ControlProps) => {
  const context = useJsonForms();
  const [fetchedOptions, setFetchedOptions] = useState<any>([]);
  const labelKey = props.uischema.options?.labelKey || 'label';
  const valueKey = props.uischema.options?.valueKey || 'value';
  const dataPath = props.uischema.options?.dataPath;
  const isPairedField = props.uischema.options?.isPairedField || false;
  const pairedFieldPath = props.uischema.options?.pairedFieldPath || '';
  const fieldPath = props.uischema.options?.fieldPath;

  let resolvedPairedFieldPath = pairedFieldPath;

  let dependsOnPrefix = '';
  const dependentOn = props.uischema.options?.dependentOn;

  /**
   * Calculates the path of the dependent on field and paired filed based on the fieldPath and the index of the field.
   * Used when conditional pickers are nested inside filed arrays.
   */
  if (fieldPath) {
    const fieldName = getFieldNameFromPathPattern(fieldPath);
    if (fieldName) {
      const path = props.path;
      const index = getIndexFromPathPattern(path, fieldPath);
      dependsOnPrefix = fieldPath.replace('${index}.' + fieldName, `${index}.`);
      resolvedPairedFieldPath = fieldPath.replace(
        '${index}.' + fieldName,
        `${index}.` + pairedFieldPath
      );
    }
  }
  const dependentOnWithPath = `${dependsOnPrefix}${
    props.uischema.options?.dependentOn || ''
  }`;
  const dependencyKey = get(context?.core?.data, dependentOnWithPath);

  let baseURL = props.uischema.options?.baseURL;
  const endpointPath = props.uischema.options?.endpointPath;
  const config = context?.config || {};
  const dropdownSection = props.uischema.options?.dropdownSection || {};

  const headers = getHeaders(context?.config?.apiHeaders);
  headers.append('content-type', 'application/json;charset=UTF-8');

  // if one select field is dependend on another field so, after selection of first field,
  // we can clear the value of second dependent field.

  const onChangeHandler = (path: string, value: any) => {
    if (isPairedField) {
      props.handleChange(resolvedPairedFieldPath, '');
    }
    props.handleChange(path, value ? value : undefined);
  };

  let apiURL: any = '';
  const loadEnumData = (apiURL: any) => {
    document.getElementsByTagName('body')[0].style.cursor = 'progress';
    fetch(apiURL, {
      method: 'GET',
      headers,
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        const options = formatDynamicEnumResponse(
          data,
          dataPath,
          labelKey,
          valueKey
        );
        setFetchedOptions(options);
      })
      .finally(() => {
        document.getElementsByTagName('body')[0].style.cursor = 'default';
      });
  };
  if (dependentOn || dependencyKey) {
    useEffect(() => {
      if (dependencyKey) {
        const params: any = {};
        params[dependentOn] = dependencyKey;
        apiURL = getBaseUrl(baseURL, params);
        if (apiURL === baseURL) {
          apiURL = apiURL + dependencyKey;
        }

        if (endpointPath) {
          const pathKey = getEndpointName(endpointPath, config);
          const endpoint = get(config, pathKey, '');
          apiURL = endpoint + apiURL;
        }
        loadEnumData(apiURL);
      }
    }, [dependencyKey]);
  } else {
    useEffect(() => {
      if (baseURL && fetchedOptions.length === 0) {
        let endpoint = '';
        if (endpointPath) {
          const pathKey = getEndpointName(endpointPath, config);
          endpoint = get(config, pathKey, '');
        }
        apiURL = endpoint + baseURL;
      }
      loadEnumData(apiURL);
    }, [baseURL]);
  }

  return (
    <SpectrumInputControl
      {...props}
      options={fetchedOptions}
      input={InputEnum}
      enumSectionProps={dropdownSection}
      handleChange={onChangeHandler}
    />
  );
};

export const spectrumConditionalEnumControlTester: RankedTester = rankWith(
  3,
  uiTypeIs('ConditionalPicker')
);

export default withJsonFormsEnumProps(SpectrumConditionalEnumControl);
